import { useState, useEffect } from 'react';
import {
  Field,
  Label,
  Select,
  SelectItem,
  Checkbox,
} from '@acb_devel/design-system';
import { useForm, Controller } from 'react-hook-form';
import type { EventType } from '@services/eventsTypes';
import type { EventForm, Event } from '@services/events';
import { getEventsTypes } from '@services/eventsTypes';
import { getEventsAreas } from '@services/eventsAreas';
import type { EventArea } from '@services/eventsAreas';
import { Button } from '@acb_devel/design-system';
import { formatDateToDatetimeLocal } from '@utils/date';

import styles from './EventForm.module.scss';

export function EventForm({
  onSubmit,
  defaultValues,
  submitButtonLabel = 'Crear evento',
}: {
  onSubmit: (event: EventForm) => void;
  defaultValues?: Event;
  submitButtonLabel?: string;
}) {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [eventAreas, setEventAreas] = useState<EventArea[]>([]);

  const useFormParams = { defaultValues: {} };

  if (defaultValues) {
    useFormParams.defaultValues = {
      location: defaultValues.location,
      edition: defaultValues.edition,
      startRegistration: formatDateToDatetimeLocal(
        defaultValues.startRegistration
      ),
      endRegistration: formatDateToDatetimeLocal(defaultValues.endRegistration),
      startEvent: formatDateToDatetimeLocal(defaultValues.startEvent),
      endEvent: formatDateToDatetimeLocal(defaultValues.endEvent),
      eventTypeId: defaultValues.eventType.id.toString(),
      eventAreaIds: defaultValues.eventAreas.map((area) => area.id.toString()),
    } as EventForm;
  }

  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EventForm>(useFormParams);

  const startRegistration = watch('startRegistration');
  const endRegistration = watch('endRegistration');
  const startEvent = watch('startEvent');
  const endEvent = watch('endEvent');

  useEffect(() => {
    getEventsTypes()
      .then((types) => setEventTypes(types))
      .catch(console.error);

    getEventsAreas()
      .then((areas) => setEventAreas(areas))
      .catch(console.error);
  }, []);

  function onSubmitWrapper(event: React.FormEvent) {
    event.preventDefault();

    handleSubmit((data) => {
      data.startRegistration = new Date(data.startRegistration).toISOString();
      data.endRegistration = new Date(data.endRegistration).toISOString();
      data.startEvent = new Date(data.startEvent).toISOString();
      data.endEvent = new Date(data.endEvent).toISOString();
      data.eventAreaIds = data.eventAreaIds || [];

      onSubmit(data);
    })();
  }

  return (
    <>
      <form className={styles.form}>
        <div>
          <Label htmlFor="startRegistration" required>
            Fechas de inscripción
          </Label>
          <div className={styles.form__halfWidth}>
            <Field
              id="startRegistration"
              type="datetime-local"
              label="Desde"
              placeholder="dd/mm/aaaa"
              {...register('startRegistration', {
                required: 'Por favor, introduce una fecha de inicio.',
                validate: (value) => {
                  if (!value) {
                    return true;
                  }

                  if (endRegistration && startRegistration >= endRegistration) {
                    return 'La fecha de inicio debe ser anterior a la fecha de fin.';
                  }

                  if (startEvent && startEvent < startRegistration) {
                    return 'La fecha de inicio del evento debe ser posterior a la fecha de inicio de las inscripciones.';
                  }

                  return true;
                },
              })}
              errorMessage={errors.startRegistration?.message}
            />
            <Field
              id="endRegistration"
              type="datetime-local"
              label="Hasta"
              placeholder="dd/mm/aaaa"
              {...register('endRegistration', {
                required: 'Por favor, introduce una fecha de fin.',
                validate: (value) => {
                  if (!value) {
                    return true;
                  }

                  if (endEvent && startEvent < endRegistration) {
                    return 'La fecha fin de las inscripciones debe ser anterior a la fecha de inicio del evento.';
                  }

                  return true;
                },
                onBlur: () => {
                  trigger('startRegistration');
                  trigger('endRegistration');
                },
              })}
              errorMessage={errors.endRegistration?.message}
            />
          </div>
        </div>
        <Controller
          name="eventTypeId"
          control={control}
          rules={{ required: 'Selecciona un tipo de evento' }}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              {...field}
              id="id"
              label="Tipo de evento"
              placeholder="Selecciona una opción"
              required
              errorMessage={errors.eventTypeId?.message}
            >
              {eventTypes.map((type) => (
                <SelectItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </SelectItem>
              ))}
            </Select>
          )}
        />
        <Controller
          name="location"
          control={control}
          rules={{ required: 'Introduce una localización' }}
          render={({ field }) => (
            <Field
              id="location"
              label="Localización"
              placeholder="Por ejemplo, Gran Canaria, Badalona..."
              {...field}
              errorMessage={errors.location?.message}
            />
          )}
        />
        <Controller
          name="edition"
          control={control}
          rules={{ required: 'Introduce una edición' }}
          render={({ field }) => (
            <Field
              id="edition"
              label="Edición"
              placeholder="Por ejemplo, 2024, 2025"
              {...field}
              errorMessage={errors.edition?.message}
            />
          )}
        />
        <div>
          <Label htmlFor="name" required>
            Fechas del evento
          </Label>
          <div className={styles.form__halfWidth}>
            <Field
              id="startEvent"
              type="datetime-local"
              label="Desde"
              placeholder="dd/mm/aaaa"
              {...register('startEvent', {
                required: 'Por favor, introduce una fecha de inicio.',
                validate: (value) => {
                  if (!value) {
                    return true;
                  }

                  if (endEvent && startEvent >= endEvent) {
                    return 'La fecha de inicio debe ser anterior a la fecha de fin.';
                  }

                  return true;
                },
                onBlur: () => {
                  trigger('startRegistration');
                  trigger('endRegistration');
                  trigger('endEvent');
                  trigger('startEvent');
                },
              })}
              errorMessage={errors.startEvent?.message}
            />
            <Field
              id="endEvent"
              type="datetime-local"
              label="Hasta"
              placeholder="dd/mm/aaaa"
              {...register('endEvent', {
                required: 'Por favor, introduce una fecha de fin.',
                validate: (value) => {
                  if (!value) {
                    return true;
                  }

                  if (endRegistration && startRegistration >= endEvent) {
                    return 'La fecha fin del evento debe ser posterior a la fecha de inicio de las inscripciones.';
                  }

                  return true;
                },
                onBlur: () => {
                  trigger('startEvent');
                  trigger('endEvent');
                },
              })}
              errorMessage={errors.endEvent?.message}
            />
          </div>
        </div>
        <Label htmlFor="eventAreas">Áreas disponibles</Label>
        <Controller
          name="eventAreaIds"
          control={control}
          // rules={{
          //   required: 'Por favor, selecciona al menos un área.',
          // }}
          render={({ field }) => (
            <div className={styles.form__areasList}>
              {eventAreas.map((area) => (
                <Checkbox
                  key={area.id}
                  id={`area-${area.id}`}
                  label={area.name}
                  checked={field.value?.includes(area.id.toString())}
                  onCheckedChange={(checked) => {
                    const currentValues = field.value ?? [];

                    if (checked) {
                      field.onChange([...currentValues, area.id.toString()]);
                    } else {
                      field.onChange(
                        currentValues.filter(
                          (selectedArea) =>
                            selectedArea.toString() !== area.id.toString()
                        )
                      );
                    }
                  }}
                />
              ))}
            </div>
          )}
        />
        <div className={styles.form__actions}>
          <Button
            variant="solid"
            color="primary"
            type="submit"
            onClick={onSubmitWrapper}
          >
            {submitButtonLabel}
          </Button>
        </div>
      </form>
    </>
  );
}
